import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabaseClient';

// Is the store paused right now? Staff flip this from the admin (sv_set_store_paused);
// everyone else sees the "we're updating our inventory" page instead of the shop.
//
// Fails OPEN on purpose. If the status call errors, the shop shows as normal:
// the checkout function refuses orders while paused no matter what the browser
// believes, so a wrong "open" here can never take an order, but a wrong
// "paused" would hide a working store.
const StoreStatusContext = createContext(null);

const POLL_MS = 60 * 1000;

export function StoreStatusProvider({ children }) {
  const [status, setStatus] = useState({ paused: false, pausedAt: null, loaded: false });

  const refresh = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('sv_store_status');
      if (error) return;
      const row = Array.isArray(data) ? data[0] : data;
      setStatus({ paused: !!row?.paused, pausedAt: row?.paused_at || null, loaded: true });
    } catch { /* stay on the last known state */ }
  }, []);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); };
  }, [refresh]);

  const value = useMemo(() => ({ ...status, refresh, setStatus }), [status, refresh]);
  return <StoreStatusContext.Provider value={value}>{children}</StoreStatusContext.Provider>;
}

export function useStoreStatus() {
  const ctx = useContext(StoreStatusContext);
  if (!ctx) throw new Error('useStoreStatus must be used within StoreStatusProvider');
  return ctx;
}
